import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AdminNav from '../../components/admin/AdminNav';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';

import api from '../../utils/api';
import { getCurrentUser } from '../../utils/auth';

const EPSILON = 0.001;
const GROUPS_PER_PAGE = 10;

export default function AdminGroupsPage() {
  /** ===============================
   *  STATE
   * =============================== */
  const [groups, setGroups] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [addMemberInput, setAddMemberInput] = useState({});
  const [removeMemberSelected, setRemoveMemberSelected] = useState({});

  const [confirmRemove, setConfirmRemove] = useState({
    isOpen: false,
    groupId: null,
    email: null,
  });

  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  /** ===============================
   *  AUTH + SCOPE
   * =============================== */
  const currentUser = getCurrentUser();
  const role = (currentUser?.role || '').toLowerCase();
  const isSupplierScoped = role === 'supplieradmin' && !!currentUser?.supplierId;
  const scopedSupplierId = isSupplierScoped ? currentUser.supplierId : null;

  /** ===============================
   *  API: FETCH GROUPS
   * =============================== */
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get('/api/groups');
      let list = [];

      if (Array.isArray(res.data)) {
        list = res.data;
      } else if (Array.isArray(res.data.groups)) {
        list = res.data.groups;
      }

      if (scopedSupplierId) {
        list = list.filter((g) => {
          const gid = g.supplierId?._id || g.supplierId;
          return String(gid) === String(scopedSupplierId);
        });
      }

      setGroups(list);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setLoadError('Failed to load groups');
      toast.error('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  }, [scopedSupplierId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  /** ===============================
   *  HELPERS
   * =============================== */
  const safeGroups = useMemo(() => (Array.isArray(groups) ? groups : []), [groups]);

  // 🔍 Filter by status + search query
  const filtered = useMemo(() => {
    let list = safeGroups;

    if (statusFilter === 'effective') {
      list = list.filter((g) => {
        const rem = Number(g.remainingCost ?? 0);
        if (g.status === 'closed') return false;
        return rem > EPSILON;
      });
    } else if (statusFilter !== 'all') {
      list = list.filter((g) => g.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((g) => {
        const supplier = g.supplierId?.name?.toLowerCase() || '';
        const groupName = g.name?.toLowerCase() || '';
        const state = g.state?.toLowerCase() || '';
        return supplier.includes(q) || groupName.includes(q) || state.includes(q);
      });
    }

    return list;
  }, [safeGroups, statusFilter, searchQuery]);

  const indexOfLast = currentPage * GROUPS_PER_PAGE;
  const indexOfFirst = indexOfLast - GROUPS_PER_PAGE;
  const currentGroups = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / GROUPS_PER_PAGE);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'full':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMemberEmail = (member) => {
    if (!member) return '';
    if (typeof member === 'string') return member;
    if (member.email) return member.email;
    return String(member);
  };

  /** ===============================
   *  ACTION HANDLERS (ADD / REMOVE MEMBERS)
   * =============================== */
  const handleAddMember = async (groupId) => {
    const email = (addMemberInput[groupId] || '').trim();
    if (!email) return toast.error('Enter a valid email');

    const group = groups.find((g) => g._id === groupId);
    if (!group) return toast.error('Group not found');

    const existsInMembers = (group.members || []).some(
      (m) => getMemberEmail(m).toLowerCase() === email.toLowerCase()
    );
    const existsInInvited = (group.invitedEmails || []).some(
      (e) => e.toLowerCase() === email.toLowerCase()
    );

    if (existsInMembers || existsInInvited) return toast.error('Member already added or invited');

    try {
      const res = await api.patch(`/api/groups/${groupId}/members/add`, { email });
      const returnedGroup = res.data.group || res.data;
      setGroups((prev) => prev.map((g) => (g._id === groupId ? returnedGroup : g)));
      setAddMemberInput((prev) => ({ ...prev, [groupId]: '' }));
      toast.success(`Added/invited ${email}`);
    } catch (err) {
      console.error('Add member error', err);
      toast.error(err?.response?.data?.message || 'Failed to add member');
      fetchGroups();
    }
  };

  const handleRemoveMemberConfirmed = async (groupId, email) => {
    try {
      const res = await api.patch(`/api/groups/${groupId}/members/remove`, { email });
      const returnedGroup = res.data.group || res.data;
      setGroups((prev) => prev.map((g) => (g._id === groupId ? returnedGroup : g)));
      setRemoveMemberSelected((prev) => ({ ...prev, [groupId]: '' }));
      toast.success(`Removed ${email}`);
    } catch (err) {
      console.error('Remove member error', err);
      toast.error(err?.response?.data?.message || 'Failed to remove member');
      fetchGroups();
    }
  };

  const requestRemoveMember = (groupId, email) => {
    if (!email) return toast.error('Select a member to remove');
    setConfirmRemove({ isOpen: true, groupId, email });
  };

  const confirmRemoveMember = () => {
    const { groupId, email } = confirmRemove;
    if (!groupId || !email) return setConfirmRemove({ isOpen: false, groupId: null, email: null });
    handleRemoveMemberConfirmed(groupId, email);
    setConfirmRemove({ isOpen: false, groupId: null, email: null });
  };

  const cancelRemoveMember = () => setConfirmRemove({ isOpen: false, groupId: null, email: null });

  const openSupplierModal = (supplier) => {
    if (!supplier) return;
    setSelectedSupplier(supplier);
    setSupplierModalOpen(true);
  };
  const closeSupplierModal = () => {
    setSelectedSupplier(null);
    setSupplierModalOpen(false);
  };

  /** ===============================
   *  RENDER
   * =============================== */
  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h1 className="text-2xl font-semibold text-gray-800">Manage Groups</h1>
          <Link
            to="/admin/create-group"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            + Create Group
          </Link>
        </div>

        {/* Filters + Search */}
        <div className="mb-4 flex gap-4 flex-wrap items-center">
          <div>
            <label className="mr-2 text-gray-700">Filter by status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border px-3 py-1 rounded"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="full">Full</option>
              <option value="closed">Closed</option>
              <option value="effective">Effective</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Search by group, supplier, or state..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="border px-3 py-1 rounded flex-grow max-w-sm"
          />
          <div className="text-sm italic self-center">
            <em>Effective status is computed from remaining cost (overrides stale full if &gt;0).</em>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-gray-600">Loading groups...</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-600">{loadError || 'No groups found.'}</p>
        ) : (
          <>
            <table className="w-full bg-white border rounded shadow">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-left">Group Name</th>
                  <th className="p-3 text-left">Supplier</th>
                  <th className="p-3 text-left">State</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left max-w-xs">Members</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentGroups.map((group) => {
                  const rem = Number(group.remainingCost ?? 0);
                  const effectiveStatus = group.status === 'closed' ? 'closed' : rem > EPSILON ? 'open' : 'full';
                  const memberOptions = [
                    ...(group.members || []).map((m) => {
                      const email = getMemberEmail(m);
                      const idPart = typeof m === 'object' && m._id ? m._id : email;
                      return { key: `member-${idPart}`, value: email, label: email };
                    }),
                    ...((group.invitedEmails || [])
                      .filter((e) => !(group.members || []).some((m) => getMemberEmail(m).toLowerCase() === e.toLowerCase()))
                      .map((e) => ({ key: `invite-${e}`, value: e, label: `${e} (invited)` }))),
                  ];

                  return (
                    <tr key={group._id} className="border-t hover:bg-gray-50 align-top">
                      <td className="p-3">{group.name}</td>
                      <td className="p-3">
                        {group.supplierId?.name ? (
                          <button
                            type="button"
                            onClick={() => openSupplierModal(group.supplierId)}
                            className="text-blue-600 hover:underline focus:outline-none"
                            title="View Supplier Details"
                          >
                            {group.supplierId.name}
                          </button>
                        ) : 'N/A'}
                      </td>
                      <td className="p-3">{group.state}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(effectiveStatus)}`}>
                            Effective: {effectiveStatus}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(group.status)}`}>
                            Stored: {group.status}
                          </span>
                        </div>
                      </td>

                      {/* Members */}
                      <td className="p-3 max-w-xs">
                        {memberOptions.length > 0 ? (
                          <div className="mb-2 flex items-center gap-2">
                            <select
                              value={removeMemberSelected[group._id] || ''}
                              onChange={(e) =>
                                setRemoveMemberSelected((prev) => ({ ...prev, [group._id]: e.target.value }))
                              }
                              className="border rounded px-2 py-1 text-sm flex-grow"
                            >
                              <option value="">Select member to remove</option>
                              {memberOptions.map((opt) => (
                                <option key={opt.key} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => requestRemoveMember(group._id, removeMemberSelected[group._id])}
                              disabled={!removeMemberSelected[group._id]}
                              className={`flex items-center justify-center px-3 py-1 rounded ${
                                removeMemberSelected[group._id]
                                  ? 'bg-red-600 hover:bg-red-700 text-white cursor-pointer'
                                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                              }`}
                              type="button"
                              aria-label="Remove member"
                              title="Remove member"
                            >
                              -
                            </button>
                          </div>
                        ) : (
                          <p className="italic text-gray-500 mb-2">No members yet.</p>
                        )}

                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Add member email..."
                            value={addMemberInput[group._id] || ''}
                            onChange={(e) => setAddMemberInput((prev) => ({ ...prev, [group._id]: e.target.value }))}
                            className="w-full border rounded px-3 py-1 text-sm"
                            autoComplete="off"
                          />
                          <button
                            onClick={() => handleAddMember(group._id)}
                            disabled={!addMemberInput[group._id]?.trim()}
                            className={`absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 rounded ${
                              addMemberInput[group._id]?.trim()
                                ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                            }`}
                            type="button"
                            title="Add member"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-3 space-x-2">
                        <Link
                          to={`/admin/groups/view/${group._id}`}
                          className="bg-blue-500 text-white px-2 py-1 rounded"
                        >
                          View
                        </Link>
                        <Link
                          to={`/admin/groups/edit/${group._id}`}
                          state={{ fromList: true }}
                          className="bg-yellow-500 text-white px-2 py-1 rounded"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center space-x-2">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded border ${
                      currentPage === i + 1
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={confirmRemove.isOpen}
        message={`Are you sure you want to remove member "${confirmRemove.email}" from this group?`}
        onConfirm={confirmRemoveMember}
        onClose={cancelRemoveMember}
      />

      <SupplierInfoModal
        isOpen={supplierModalOpen}
        supplier={selectedSupplier}
        onClose={closeSupplierModal}
      />
    </div>
  );
}
